import { AsyncPipe, Location, NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, FormsModule, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonicModule, NavController, PopoverController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { interval, of, Subject, Subscription } from "rxjs";
import { catchError, map, switchMap, take, takeUntil } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { CashoutSuccessComponent } from "src/app/components/cashout/cashout-success/cashout-success.component";
import { CashoutTooltipComponent } from "src/app/components/cashout/cashout-tooltip/cashout-tooltip.component";
import { NarratorTipsComponent } from "src/app/components/narrator-tips/narrator-tips.component";
import { ChatbotWidgetService } from "src/app/services/chatbotwidget/chatbot-widget.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { NarratorService } from "src/app/services/narrator.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { ClarityService } from "src/app/services/clarity.service";
import { HeaderComponent } from "../../../components/header/header.component";
import { DisplayCurrencyPipe } from "../../../pipe/display-currency.pipe";
import { CapitalizeFirstPipe } from "../../../pipe/capitalize-first";
import { SafeHtmlPipe } from "../../../pipe/safehtmlpipe";
import { CashOutRepositoryService } from "../cash-out-repository.service";
import { FAQModalComponent } from "src/app/components/faq-modal/faq-modal.component";

@Component({
  selector: "app-cashout",
  templateUrl: "./cashout.component.html",
  styleUrls: ["./cashout.component.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [
    HeaderComponent,
    IonicModule,
    NgClass,
    FormsModule,
    MatProgressSpinner,
    MatIcon,
    AsyncPipe,
    DisplayCurrencyPipe,
    CapitalizeFirstPipe,
    SafeHtmlPipe,
    TranslatePipe],
})
export class CashoutComponent implements OnInit, OnDestroy {
  showWidget: boolean = false;
  private widgetSubscription: Subscription;
  private navigate = inject(NavController);
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  public popoverController = inject(PopoverController);
  private location = inject(Location);
  private authService = inject(AuthService);
  private cashOutService = inject(CashOutRepositoryService);
  public translate = inject(TranslateService);
  private GTMSegment = inject(GTMSegmentService);
  private ChatbotWidgetService = inject(ChatbotWidgetService);
  private paymentDataService = inject(PaymentDataService);
  private narratorService = inject(NarratorService);
  private clarityService = inject(ClarityService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private hideTimeout: any;
  userCurrencySignal = signal("")
  userCurrencySymboleSignal = signal("")
  selectedLanguage: string = "en";
  pageTitle = "Cash Out";
  typeOfCashout = "convert";
  cashValue: number = 0;
  decimalPattern = /^-?\d+(\.\d+)?$/;
  creditValue;
  creditConversion = false;
  userBalance$ = this.authService.userCurrentBalance$;
  cashoutData$ = this.cashOutService.getCashOut();
  isPayPalCashoutSignal = signal(false);
  isGBPValueSignal = signal(0);

  errorMsg: any;

  spinnerAnimation: boolean;
  progressBar: number;
  timer$: any;
  sub: any;
  proceed = true;
  isAlertSignal = signal(false)
  email = "";
  payPalCashValue: number;
  maxValue: number;
  userWithdrawableBalance: number;
  allowDonation: boolean = false;
  isEmailValid = false;
  check: string;
  amountPayPal = new FormControl("", Validators.min(3));
  cashOutLatestStatus$ = this.cashOutService.cashOutLatestStatus$;
  quantity: any;
  quantityPayPal = "0";
  quantityCredit: string;
  processingCashOut: boolean = false;

  isCashoutMoreThan1$ = this.cashOutService.getCashOutStatus().pipe(
    map((data) => {
      return data && data.length > 1 ? true : false;
    })
  );
  protect: string;
  preferredCurrency: string = CommonVariable.preferredCurrency
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  cashoutMsgs: any;
  cashoutLowestMsg: any;
  paypalMinCashOutValue: number;
  isPayPalCurrencyINRSignal = signal(false);
  paypalCurrencySymbol: string = CommonVariable.defaultCurrencySymbol;
  isLangINR = false;
  conversionRates: Data;
  destroy$ = new Subject<void>();

  constructor(
  ) {
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.translate.get("cashout")?.subscribe(
      (msgs: string) => {
        this.cashoutMsgs = msgs;
        if (this.cashoutMsgs?.int_lowest) {
          this.paymentDataService.isDirectCurrencyConversion.set(true);
          this.authService
            .getDVFromString(this.cashoutMsgs?.int_lowest)
            .then((dvString: string) => {
              if (dvString) {
                this.cashoutLowestMsg = this.roundUpAmountFromMessage(dvString);
              } else {
                return "";
              }
            })
            .catch((err) => {
              return err;
            });
        }
      },
      (err) => {
        return err;
      }
    );
    this.isLangINR = this.preferredCurrency === 'INR';

    this.paymentDataService.getCurrencyConversion().pipe(
      takeUntil(this.destroy$)
    ).subscribe((currencyJSON: Data) => {
      if (currencyJSON) {
        this.conversionRates = currencyJSON?.currency?.conversion_rate || [];
        let getActualRate: number = this.conversionRates?.[this.preferredCurrency];
        const currency_conversion = this.isPayPalCurrencyINRSignal() ? 3 : Number(getActualRate ? getActualRate * 3 : 3)?.toFixed(2);
        this.paypalMinCashOutValue = Math.ceil(Number(currency_conversion))
      }
    });
  }
  blurOnCharity(event) {
    let value = event?.target.value;
    if (!value) {
      value = 0;
    }
    this.quantity = parseFloat(value)?.toFixed(2);
  }

  blurOnCredit(event) {
    let value = event?.target.value;
    if (!value) {
      value = 0;
    }
    this.quantityCredit = parseFloat(value)?.toFixed(2);
  }

  blurOnPayPal(event) {
    let value = event?.target.value;
    if (!value) {
      value = 0;
    }
    this.quantityPayPal = parseFloat(value)?.toFixed(2);
  }



  openChatBot() {
    this.clarityService.event("digital_assistant_opened");
    this.ChatbotWidgetService.openChatBot();
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.ChatbotWidgetService.setWidgetVisibility(true);
    this.hideTimeout = setTimeout(() => {
      this.ChatbotWidgetService.setWidgetVisibility(false);
    }, 300000);
  }

  ngOnInit() {
    this.authService?.selectedlanguage$?.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (l) => {
        this.selectedLanguage = l ? l : "en";
        if (l) {
          const selectedLanguage = l ? l : "en";
          const finalLanguage = CommonVariable.bypassLanguages.includes(selectedLanguage) ? 'en' : selectedLanguage;
          this.translate.use(finalLanguage);
        }
      },
      (err) => {
        return (err);
      }
    );
    this.authService.userDetails$?.pipe(
      takeUntil(this.destroy$),
      switchMap((user: Data) => {
        if (user) {
          const userEmail = user?.payPalEmail || user?.email;
          if (userEmail) {
            this.email = userEmail;
            this.setPayPalEmail(userEmail);
          }
        }
        this.preferredCurrency = this.isPayPalCashoutSignal()
          ? CommonVariable.preferredCurrency
          : (user?.preferred_currency ?? CommonVariable.preferredCurrency);
        this.userCurrencySignal.set(user?.preferred_currency);
    
        return this.authService.getCurrencyList$?.pipe(
          map((data: Data) => ({ user, currencyData: data }))
        );
      })
    ).subscribe(
      ({ currencyData }) => {
        const CurrencyDetails = currencyData?.details?.find(
          (x: any) => x?.currency_code === this.preferredCurrency
        );
        this.currencySymbol = CurrencyDetails?.currency_symbol
          ?? CommonVariable.defaultCurrencySymbol;
        this.userCurrencySymboleSignal.set(CurrencyDetails?.currency_symbol);
      },
      (err) => { return err; }
    );
    
    this.widgetSubscription = this.ChatbotWidgetService.widgetVisibility$.subscribe(
      visible => {
        this.showWidget = visible;
        if (this.hideTimeout) {
          clearTimeout(this.hideTimeout);
        }
        if (visible) {
          this.hideTimeout = setTimeout(() => {
            this.ChatbotWidgetService.setWidgetVisibility(false);
          }, 600000);
        }
      }
    );
    this.check = `<p class="tooltip-msg">
        To protect against fraud, winnings are not available to be withdrawn until 72 hours after the funds were won.
      </p>`;
    this.translate.get("cashout.protect")?.subscribe(
      (text: string) => {
        this.protect = text;
      },
      (err) => {
        return (err);
      }
    );
    this.translate.get(["cashout.pageTitle"])?.subscribe(
      (res: string) => {
        this.pageTitle = res["cashout.pageTitle"];
      },
      (err) => {
        return (err);
      }
    );

    this.userBalance$.subscribe({
      next: (data: Data) => {
        this.userWithdrawableBalance = data?.preferred_currency?.withdraw_amount
          ? data?.preferred_currency?.withdraw_amount
          : data?.withdraw_amount ? data?.withdraw_amount : 0;
      },
      error: (err) => {
        return (err);
      }
    });
  }

  ionViewWillEnter() {
    // this.narratorShow()
  }
  public narratorData: Data;
  public narratorShow(): void {
    this.narratorService?.getNarratorTipsHome().pipe(
      catchError((error) => {
        this.authService.errorHandlerLogs('narratorShow fetch error:', error);
        return of([]);
      })
    ).subscribe(
      (response: any[]) => {
        this.narratorData = []; // reset the data array to empty array for the next page load

        // Ensure the response is valid and is an array
        if (Array.isArray(response) && response.length > 0) {
          for (const tip of response) {
            // Check if the current page is the same as the last page name && seen is false
            if (
              tip?.setting?.page_name === this.narratorService?.getCurrentPageURL() &&
              tip?.setting?.seen === false
            ) {
              this.narratorData = tip;
              break;
            }
          }
        }

        // Display the narrator tips dialog if applicable
        if (
          this.narratorData?.setting?.seen === false &&
          this.narratorData?.setting?.page_name === this.narratorService?.getCurrentPageURL()
        ) {
          this.matDialog.closeAll();
          this.matDialog.open(NarratorTipsComponent, {
            panelClass: "narrator-tip",
            height: "100%",
            width: "100%",
            data: this.narratorData,
          });
          this.changeDetectorRef.markForCheck();
        }
      },
      (error) => {
        this.authService.errorHandlerLogs('narratorShow subscription error:', error);
      }
    );
  }

  selectOption(option: string) {
    this.cashValue = 0;
    this.quantity = "0";
    this.quantityPayPal = "0";
    this.quantityCredit = "0";
    if (option !== this.typeOfCashout) {
      this.typeOfCashout = option;
    }
    this.errorMsg = "";
    if (option === "donation" || option === "convert") {
      this.currancyUpdate()
      this.isPayPalCashoutSignal.set(false);
      this.isPayPalCurrencyINRSignal.set(false);
    }
    if (option === "paypal" && this.preferredCurrency === 'INR') {
      this.payPalCurrancyUpdate()
      this.isPayPalCashoutSignal.set(true);
      this.isPayPalCurrencyINRSignal.set(true);
    }
  }

  openCashoutStatus() {
    this.navigate.navigateForward("cashout/cashoutStatus");
  }

  setPayPalEmail(value: string) {
    this.email = value;

    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (re.test(String(this.email)?.toLowerCase())) {
      this.isEmailValid = true;
    } else {
      this.isEmailValid = false;
    }
  }

  setPayPalCash(value, _cashoutData) {
    this.paymentDataService.isDirectCurrencyConversion.set(false);
    this.payPalCashValue = Number(value);

    if (value == "" || value < 3) {
      const message = this.cashoutMsgs?.["please_enter"];
      if (this.isPayPalCurrencyINRSignal()) {
        this.errorMsg = `${message} ${this.paypalCurrencySymbol}${3}`
      } else {
        this.paymentDataService.isDirectCurrencyConversion.set(true);
        this.paymentDataService.getBeautifiedCurrency("3.00", this.preferredCurrency).then((beautifiedAmt) => {
          this.errorMsg = `${message} ${this.currencySymbol + Math.ceil(Number(beautifiedAmt))}`;
        }).catch((error) => {
          this.authService.errorHandlerLogs('Error in getBeautifiedCurrency subscription:', error);
        })
      }
      this.creditConversion = false;
    } else {
      const fxRate = this.conversionRates?.[this.preferredCurrency];
      const calculatedMaxLimit = fxRate && !this.isPayPalCurrencyINRSignal() ? Math.floor(2500 * fxRate) : 2500;
      this.maxValue = calculatedMaxLimit;

      if (this.userWithdrawableBalance < Number(value) && calculatedMaxLimit >= Number(value)) {
        this.errorMsg = this.cashoutMsgs?.["You_do_not_have"];
        this.creditConversion = false;
      } else if (value && Number(value) < this.paypalMinCashOutValue) {
        let dvMessage = this.cashoutMsgs?.["You_must"];
        this.paymentDataService.isDirectCurrencyConversion.set(true);
        this.processDynamicVariable(dvMessage).then((errorMessage: any) => {
          this.errorMsg = this.roundUpAmountFromMessage(errorMessage);
        }).catch((error) => {
          return error;
        });
        this.creditConversion = false;
      } else if (Number(value) > calculatedMaxLimit) {
        const currencySymbol = this.isPayPalCurrencyINRSignal() ? this.paypalCurrencySymbol : this.currencySymbol;
        const formattedAmount = `${currencySymbol}${Math.floor(calculatedMaxLimit).toLocaleString()}`;
        const errorTemplate = this.cashoutMsgs?.["Cashout_Maxims"] || "The maximum PayPal cashout allowed is {value}.";
        this.errorMsg = errorTemplate?.replace('{value}', formattedAmount);
        this.creditConversion = false;
      } else if (!this.decimalPattern.test(value)) {
        this.errorMsg = this.cashoutMsgs?.["Valid_number"];
        this.creditConversion = false;
      } else {
        this.creditConversion = true;
        this.errorMsg = "";
      }
    }
  }

  processDynamicVariable(dvMessage) {
    return new Promise((resolve, reject) => {
      if (dvMessage) {
        this.authService
          .getDVFromString(dvMessage)
          .then((message: string) => {
            if (message) {
              resolve(message);
            } else {
              return "";
            }
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }

  async showTooltip(events: any) {
    const tooltip = await this.popoverController.create({
      component: CashoutTooltipComponent,
      event: events,
    });

    return await tooltip.present();
  }

  startSpinner() {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$?.subscribe(
      (sec) => {
        this.progressBar = 0 + sec;
      },
      (err) => {
        return (err);
      }
    );
  }

  cashOutModal(type: string) {
    this.clarityService.event("withdraw_winnings_requested");

    if (type === 'paypal')
      this.GTMSegment.HotJarEvent('user_cashout');
    else if (type === 'donation')
      this.GTMSegment.HotJarEvent('user_donate_winnings');
    else if (type === 'conversion')
      this.GTMSegment.HotJarEvent('user_convert_credit');

    this.processingCashOut = true;
    if (type === "conversion") {
      this.cashOutService.updateCreditCashOut(this.cashValue)?.subscribe(
        (data) => {
          this.spinnerComplete();
          if (data?.status === "rejected") {
            this.authService.getConstantsMsg(data?.error_code).then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              }
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }).catch((error) => {
              return error;
            });
            this.spinnerStop();
            this.processingCashOut = false;
            return;
          }
          if (data && data?.response && data?.response?.status === "SUCCESS") {
            this.onSuccessCashOut(type);
          }
          this.spinnerStop();
          this.processingCashOut = false;
        },
        (err) => {
          return (err);
        }
      );
    } else if (type === "paypal") {
      this.cashOutService.updatePayPalCashOut(this.payPalCashValue, this.email)?.subscribe(
        (data) => {
          this.spinnerComplete();
          this.cashOutService.updatePayPalCashOutEmail(this.email);
          if (data?.status === "rejected") {
            this.authService.getConstantsMsg(data?.error_code).then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              }
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }).catch((error) => {
              return error;
            });
            this.spinnerStop();
            this.processingCashOut = false;
            return;
          }
          if (data && data?.response && data?.response?.status && data?.response?.status === "SUCCESS") {
            this.onSuccessCashOut(type);
          }
          this.spinnerStop();
          this.processingCashOut = false;
        },
        (err) => {
          return (err);
        }
      );
    } else if (type === "donation") {
      this.cashOutService.updateCharityCashOut(this.cashValue)?.subscribe(
        (data) => {
          this.spinnerComplete();
          if (data?.status === "rejected") {
            this.authService.getConstantsMsg(data?.error_code).then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              }
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }).catch((error) => {
              return error;
            });
            this.spinnerStop();
            this.processingCashOut = false;
            return;
          }
          if (data && data?.response && data?.response?.status && data?.response?.status === "SUCCESS") {
            this.onSuccessCashOut(type);
          }
          this.spinnerStop();
          this.processingCashOut = false;
        },
        (err) => {
          return (err);
        }
      );
    }
  }

  private spinnerComplete() {
    this.progressBar === 100;
    this.sub?.unsubscribe();
  }

  private spinnerStop() {
    setTimeout(() => {
      this.spinnerAnimation = false;
      this.proceed = false;
      setTimeout(() => {
        this.proceed = true;
      }, 2000);
    }, 300);
  }

  private onSuccessCashOut(type: string) {
    const cashoutSuccessDialog = this.matDialog.open(CashoutSuccessComponent, {
      panelClass: "dialog-size",
      backdropClass: "cashout-back-drop-color",
      data: { type: type },
    });

    cashoutSuccessDialog.afterClosed().subscribe(
      (response) => {
        if (response) {
          if (response === "conversion") {
            this.router.navigateByUrl("/mainTabs/tabs/store");
          } else if (response === "paypal") {
            this.openCashoutStatus();
          } else if (response === "donation") {
            //facebook
          } else if (response === "account") {
            this.router.navigateByUrl("/mainTabs/tabs/account");
          }
        }
      },
      (err) => {
        return (err);
      }
    );
  }

  learnMoreAboutCharity() {
    this.router.navigateByUrl("mainTabs/tabs/charity");
  }

  goBack() {
    this.location.back();
  }

  numericOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  cashConversion(value, credit_multiplier, cashoutData) {
    let maxValue = cashoutData?.int_credit_max_limit?.[this.preferredCurrency]
      ? cashoutData?.int_credit_max_limit?.[this.preferredCurrency]
      : cashoutData?.credit_max_limit;
    this.cashValue = value;
    this.maxValue = maxValue;
    this.userBalance$?.subscribe(
      (data: any) => {
        const defaultWithdrawAmt = 0;
        /*  this.userWithdrawableBalance = data?.preferred_currency?.withdraw_amount
          ? data?.preferred_currency?.withdraw_amount
          : data?.withdraw_amount ? data?.withdraw_amount : defaultWithdrawAmt; */
        this.userWithdrawableBalance = data?.withdraw_amount ? data?.withdraw_amount : defaultWithdrawAmt;
        if (value === "" || value <= 0) {
          this.errorMsg = this.cashoutMsgs?.["please_enter"] + ` ${this.currencySymbol}0.00.`;
          this.creditConversion = false;
        } else if (this.userWithdrawableBalance < Number(value) && maxValue >= Number(value)) {
          this.errorMsg = this.cashoutMsgs?.["You_cannot_convert"];
          this.creditConversion = false;
        } else if (maxValue && maxValue < Number(value)) {
          this.errorMsg = this.cashoutMsgs?.["Maximum withdrawable"] + ` ${this.currencySymbol + maxValue}`;
          this.creditConversion = false;
        } else if (!this.decimalPattern.test(value)) {
          this.errorMsg = this.cashoutMsgs?.["Valid_number"];
          this.creditConversion = false;
        } else {
          this.creditValue = Number(value) * credit_multiplier;
          this.creditConversion = true;
          this.errorMsg = "";
        }
      },
      (err) => {
        return (err);
      }
    );
  }

  setDonationValue(value, cashoutData) {
    let maxValue = cashoutData?.int_charity_max_limit?.[this.preferredCurrency]
      ? cashoutData?.int_charity_max_limit?.[this.preferredCurrency]
      : cashoutData?.charity_max_limit;
    this.cashValue = value;
    this.maxValue = maxValue;
    this.userBalance$?.pipe(take(1)).subscribe(
      (data: any) => {
        const defaultWithdrawAmt = 0;
        /* this.userWithdrawableBalance = data?.preferred_currency?.withdraw_amount
          ? data?.preferred_currency?.withdraw_amount
          : data?.withdraw_amount ? data?.withdraw_amount : defaultWithdrawAmt; */
        this.userWithdrawableBalance = data?.withdraw_amount ? data?.withdraw_amount : defaultWithdrawAmt;
        if (value === "" || value <= 0) {
          this.errorMsg = this.cashoutMsgs?.["please_enter"] + ` ${this.currencySymbol}0.00.`;
          this.allowDonation = false;
        } else if (this.userWithdrawableBalance < Number(value) && maxValue >= Number(value)) {
          this.errorMsg = this.cashoutMsgs?.["You_cannot_convert"];
          this.allowDonation = false;
        } else if (maxValue && maxValue < Number(value)) {
          this.errorMsg = this.cashoutMsgs?.["Maximum"] + ` ${this.currencySymbol + maxValue}`;
          this.allowDonation = false;
        } else if (!this.decimalPattern.test(value)) {
          this.errorMsg = this.cashoutMsgs?.["Valid_number"];
          this.allowDonation = false;
        } else {
          this.allowDonation = true;
          this.errorMsg = "";
        }
      },
      (err) => {
        return err;
      }
    );
  }

  charityCashOut() { }

  creditCashOut() { }

  payPalCashOut() { }

  payPalCurrancyUpdate() {
    this.currencySymbol = this.currencySymbol;
    this.preferredCurrency = this.preferredCurrency;

    this.isAlertSignal.set(true);
    this.authService.isPayPalSignal.set(true);

    const currency$ = this.paymentDataService.getCurrencyConversion();
    const balance$ = this.userBalance$;

    // Fetch PayPal min cashout value
    currency$?.pipe(take(1)).subscribe((currencyJSON: any) => {
      let getActualRate: number = currencyJSON?.currency?.conversion_rate?.[this.preferredCurrency];
      const currency_conversion = this.isPayPalCurrencyINRSignal() ? 3 : Number(getActualRate ? getActualRate * 3 : 3)?.toFixed(2);
      this.paypalMinCashOutValue = Math.ceil(Number(currency_conversion))
    });

    // Calculate GBP equivalent from INR balance
    balance$?.pipe(take(1)).subscribe((data: any) => {
      const userINR = data?.preferred_currency?.withdraw_amount;
      currency$?.pipe(take(1)).subscribe((currencyJSON: Data) => {
        const gbpValue = this.getGBPValueFromINR(userINR, currencyJSON);
        this.isGBPValueSignal.set(gbpValue);
      });
    });

    // Fetch translated cashout messages
    this.translate.get("cashout")?.pipe(take(1)).subscribe({
      next: (msgs: string) => {
        this.cashoutMsgs = msgs;
        const msg = this.cashoutMsgs?.int_lowest;
        if (!msg) return;
        this.paymentDataService.isDirectCurrencyConversion.set(true);
        this.authService.getDVFromString(msg)
          .then((dvString: string) => this.cashoutLowestMsg = this.roundUpAmountFromMessage(dvString))
          .catch((err) => this.authService.errorHandlerLogs('payPalCurrancyUpdate DV error:', err));
        },
        error: (err) => this.authService.errorHandlerLogs('payPalCurrancyUpdate translate error:', err)
    });
  }

  // Update currancy ans currancy symbole
  currancyUpdate() {
    this.currencySymbol = this.userCurrencySymboleSignal();
    this.preferredCurrency = this.userCurrencySignal();
  }

  // Get GPB Currancy conversion value
  getGBPValueFromINR(targetINR: number, currencyData: Data): number | null {
    let convertAmt: number | string = 0;

    let getBeautify: Data = currencyData?.currency?.beautify;
    let getActualRate: number = currencyData?.currency?.conversion_rate?.[this.preferredCurrency];

    if (getBeautify[targetINR]) {
      convertAmt = getBeautify[targetINR]?.[this.preferredCurrency];
    } else {
      convertAmt = Number(getActualRate ? Number(targetINR) / getActualRate : 0);
    }
    return Number(convertAmt);
  }

  onDeclinedClick(): void {
    const urls: Record<string, string> = {
      it: "https://support.lottosocial.com/perche-il-mio-prelievo-e-stato-rifiutato/",
      es: "https://support.lottosocial.com/por-que-se-ha-rechazado-mi-retiro/",
      fr: "https://support.lottosocial.com/pourquoi-mon-retrait-a-t-il-ete-refuse/",
      en: "https://support.lottosocial.com/why-has-my-cashout-been-declined/"
    };

    // fallback to English if selectedLanguage not in mapping
    const url = urls[this.selectedLanguage] ?? urls?.en;

    this.matDialog.open(FAQModalComponent, {
      panelClass: "faq-modal",
      data: url,
      hasBackdrop: true,
      disableClose: false,
    });
  }

  getTranslatedText(statusText: string) {
    const translated = this.cashOutService.getTranslatedStatus(statusText);
    return translated;
  }

  private roundUpAmountFromMessage(message: string) {
    return message.replace(
      /([€$£])([\d.]+)/,
      (_, symbol, amount) => `${symbol}${Math.ceil(Number(amount))}`
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete()
  }
}
